"use client";
import { useId } from "react";
import { formatMoney } from "./types";
import type { StorefrontFeatures } from "../features";
import type { WebshopTranslate } from "./i18n";

/** Integer minor units keep bounds and keyboard steps exact. */
export function priceFilterBounds(minimum: number, maximum: number, step: number) {
 const safeStep=Number.isSafeInteger(step)&&step>0?step:1;
 const lower=Math.max(0,Math.floor(minimum/safeStep)*safeStep);
 return { minimum:lower,maximum:Math.max(lower+safeStep,Math.ceil(maximum/safeStep)*safeStep),step:safeStep };
}
export function CatalogPriceFilter({ minimum,maximum,minInput,maxInput,onMin,onMax,onApply,onReset,preferences,currency,locale,t }: {
 minimum:number; maximum:number; minInput:string; maxInput:string;
 onMin(value:string):void;onMax(value:string):void;onApply():void;onReset():void;
 preferences:StorefrontFeatures;currency:string;locale?:string;t:WebshopTranslate;
}) {
 const id=useId(),mode=preferences.priceFilterPresentation;
 const bounds=priceFilterBounds(mode==='range'?minimum:0,mode==='range'?maximum:Math.max(100,maximum,Number(minInput)*100||0,Number(maxInput)*100||0),preferences.priceFilterStepMinor);
 const inputMinor=(value:string,fallback:number)=>{
  const amount=value.trim()===''?NaN:Number(value);
  return Number.isFinite(amount)?Math.round(amount*100):fallback;
 };
 const low=Math.min(bounds.maximum,Math.max(bounds.minimum,inputMinor(minInput,bounds.minimum)));
 const high=Math.max(low,Math.min(bounds.maximum,inputMinor(maxInput,bounds.maximum)));
 const change=(value:string,isMin:boolean)=>{
  const minor=Math.round(Number(value)*100/bounds.step)*bounds.step;
  const next=Math.min(bounds.maximum,Math.max(bounds.minimum,minor));
  (isMin?onMin:onMax)((Math.min(isMin?high:bounds.maximum,Math.max(isMin?bounds.minimum:low,next))/100).toFixed(2));
 };
 const slider=(isMin:boolean)=><input type="range" aria-label={t(isMin?'module.webshops.catalog.min_price':'module.webshops.catalog.max_price',isMin?'Minimum price':'Maximum price')}
   aria-valuemin={(isMin?bounds.minimum:low)/100} aria-valuemax={(isMin?high:bounds.maximum)/100}
   aria-valuetext={formatMoney(isMin?low:high,currency,locale)} min={bounds.minimum/100} max={bounds.maximum/100} step={bounds.step/100}
   value={(isMin?low:high)/100} onChange={e=>change(e.target.value,isMin)}/>;
 return <form className="webshops-price-filter" data-presentation={mode} onSubmit={e=>{e.preventDefault();onApply()}}><fieldset>
  <legend>{t('module.webshops.catalog.price_filter','Filter by price')}</legend>
  <label htmlFor={`${id}-min`}>{t('module.webshops.catalog.min_price','Minimum price')}</label>
  <input id={`${id}-min`} type="number" min={mode==='range'?bounds.minimum/100:0} max={maxInput||undefined} step={bounds.step/100} value={minInput} onChange={e=>onMin(e.target.value)}/>
  {mode==='separate'?slider(true):null}
  <label htmlFor={`${id}-max`}>{t('module.webshops.catalog.max_price','Maximum price')}</label>
  <input id={`${id}-max`} type="number" min={minInput||String(mode==='range'?bounds.minimum/100:0)} max={mode==='range'?bounds.maximum/100:undefined} step={bounds.step/100} value={maxInput} onChange={e=>onMax(e.target.value)}/>
  {mode==='separate'?slider(false):mode==='range'?<div className="webshops-price-range" data-presentation="range">{slider(true)}{slider(false)}</div>:null}
  <p><output>{formatMoney(low,currency,locale)} – {formatMoney(high,currency,locale)}</output></p>
  <button className="webshops-button webshops-button--secondary" type="submit">{t('module.webshops.catalog.apply_filters','Apply filters')}</button>
  <button className="webshops-link-button" type="button" onClick={onReset}>{t('module.webshops.catalog.reset_price','Reset price')}</button>
 </fieldset></form>;
}
